import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  Disclaimers as DisclaimersValue, fetchDisclaimers, updateDisclaimers,
} from '../features/disclaimers/disclaimers';
import { RootState } from '../app/store';

interface DisclaimerField {
  field: keyof DisclaimersValue;
  label: string;
  helperText: string;
  minRows: number;
}

const FIELDS: DisclaimerField[] = [
  {
    field: 'plans',
    label: 'Plans Disclaimer',
    helperText: 'Disclaimer text below the plan cards.',
    minRows: 6,
  },
  {
    field: 'checkout',
    label: 'Checkout Disclaimer',
    helperText: 'Disclaimer text shown at checkout.',
    minRows: 4,
  },
];

/**
 * The free text disclaimers. As for a router, nothing is written until the
 * operator reviews the changes in the confirmation dialog and confirms.
 */
const Disclaimers = () => {
  const dispatch = useAppDispatch();
  const disclaimersState = useAppSelector((state: RootState) => state.disclaimers);

  const [form, setForm] = useState<DisclaimersValue | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { dispatch(fetchDisclaimers()); }, []);

  useEffect(() => {
    if (disclaimersState.value) setForm({ ...disclaimersState.value });
  }, [disclaimersState.value]);

  if (form === null && disclaimersState.status === 'failed') {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3, maxWidth: 900, mx: 'auto', mt: 10,
        }}
      >
        <Alert severity="error">The disclaimers could not be loaded.</Alert>
      </Paper>
    );
  }

  if (form === null) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', p: 10, mt: 10,
      }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const current: DisclaimersValue = disclaimersState.value ?? { plans: '', checkout: '' };
  const changes = FIELDS.filter(({ field }) => (form[field] ?? '') !== (current[field] ?? ''));
  const dirty = changes.length > 0;

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await dispatch(updateDisclaimers(form)).unwrap();
      setSaved(true);
      setConfirmOpen(false);
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      setError(`Failed to save the disclaimers: ${reason}`);
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3, maxWidth: 900, mx: 'auto', mt: 10,
      }}
    >
      <Stack spacing={3}>
        <Typography variant="h6">Disclaimers</Typography>

        {error && <Alert severity="error">{error}</Alert>}
        {saved && !dirty && <Alert severity="success">Saved.</Alert>}

        {FIELDS.map(({
          field, label, helperText, minRows,
        }) => (
          <TextField
            key={field}
            label={label}
            helperText={helperText}
            value={form[field] ?? ''}
            onChange={(e) => { setForm({ ...form, [field]: e.target.value }); setSaved(false); }}
            multiline
            minRows={minRows}
            fullWidth
          />
        ))}

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            disabled={!dirty || saving}
            onClick={() => setConfirmOpen(true)}
          >
            Save changes…
          </Button>
          <Button disabled={!dirty || saving} onClick={() => setForm({ ...current })}>
            Discard changes
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => !saving && setConfirmOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Apply changes to the disclaimers?</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Current</TableCell>
                <TableCell>New</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changes.map(({ field, label }) => (
                <TableRow key={field}>
                  <TableCell>{label}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-line' }}>
                    {current[field] || '(empty)'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-line', fontWeight: 'bold' }}>
                    {form[field] || '(empty)'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>Back</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={saving}>
            {saving ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Disclaimers;
